import React from 'react';
import { EndpointUnavailableError, EraScope } from 'stores/clearinghouse.store';
import * as S from './clearinghouses.styles';

/**
 * Says, above a list, what the backend actually answered — so a missing endpoint reads as missing and
 * not as "no ERAs" (NS-3), and so the person who has to add it can see which one and with which filters.
 * Driven by the response, not by a setting: once the endpoint works, the error is gone and so is this.
 */
export function ClearinghouseUnavailableNotice({
    error,
    what,
}: {
    error: EndpointUnavailableError;
    /** What the list would show, in the words a reviewer would use. */
    what: string;
}): React.ReactElement {
    return (
        <S.Notice role="status">
            <div>
                <S.NoticeTitle>{what} can't be shown yet.</S.NoticeTitle> The backend answered{' '}
                <S.Code>{error.endpoint}</S.Code> with {error.status}
                {error.code ? (
                    <>
                        {' '}
                        (<S.Code>{error.code}</S.Code>)
                    </>
                ) : null}
                . The list below loads once it answers successfully.
            </div>
        </S.Notice>
    );
}

/** Shown when the ERA rows cannot be confirmed to belong to the clearinghouse that was clicked. */
export function ClearinghouseScopeNotice({ scope }: { scope?: EraScope }): React.ReactElement | null {
    if (scope === 'unconfirmed') {
        return (
            <S.Notice role="status">
                <div>
                    <S.NoticeTitle>Not confirmed to be this clearinghouse's ERAs.</S.NoticeTitle> The list is requested
                    with <S.Code>filterClearinghouseId</S.Code>, but its rows came back without a clearinghouse id to
                    check it against. If the backend ignores the filter, these are all of the account's ERAs.
                </div>
            </S.Notice>
        );
    }
    if (scope === 'ignored') {
        return (
            <S.Notice role="status">
                <div>
                    <S.NoticeTitle>Includes ERAs from other clearinghouses.</S.NoticeTitle> The backend returned ERAs
                    belonging to a different clearinghouse, so it is ignoring <S.Code>filterClearinghouseId</S.Code>.
                </div>
            </S.Notice>
        );
    }
    return null;
}
